import React, { useState, useEffect } from "react";
import {
  CriteriaType,
  Grade,
  CriteriaTypeDescription,
  GradeKoreanName,
  GradeColorClass,
  DataCriteriaGroupResponseType,
  DataCriteriaBulkUpdateType,
  GradeDataType,
} from "@/types/dataCriteria";
import { getAllDataCriteriaApi, bulkUpdateDataCriteriaApi } from "@/lib/api/dataCriteria";
import { message } from "antd";
import "./DataCriteriaView.css";

const DataCriteriaView: React.FC = () => {
  const [criteriaData, setCriteriaData] = useState<DataCriteriaGroupResponseType[]>([]);
  const [loading, setLoading] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, GradeDataType>>({});

  // 데이터 로드
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log("데이터 로드 시작...");
      const data = await getAllDataCriteriaApi();
      console.log("API 응답:", data);

      // 데이터가 없거나 빈 배열인 경우 기본 데이터 설정
      if (!data || data.length === 0) {
        console.log("데이터가 없음, 기본 데이터 설정");
        setCriteriaData(getDefaultData());
      } else {
        setCriteriaData(data);
      }
    } catch (error: any) {
      console.error("데이터 로드 에러:", error);
      console.error("에러 상세:", error.response?.data || error.message);
      message.error(
        `데이터 로드 실패: ${error.response?.data?.message || error.message || "알 수 없는 오류"}`
      );

      // 에러 시 기본 데이터로 화면 표시
      setCriteriaData(getDefaultData());
    } finally {
      setLoading(false);
    }
  };

  // 기본 데이터 구조
  const getDefaultData = (): DataCriteriaGroupResponseType[] => {
    return [
      {
        criteriaType: CriteriaType.SINGLE_DEVICE,
        criteriaTypeDescription: CriteriaTypeDescription[CriteriaType.SINGLE_DEVICE],
        grades: [
          {
            id: 1,
            criteriaType: CriteriaType.SINGLE_DEVICE,
            criteriaTypeDescription: "",
            grade: Grade.GOOD,
            gradeKoreanName: "좋음",
            h2sValue: 5.0,
            nh3Value: 50.0,
            ch4Value: 3000.0,
            comparisonType: "이하",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 2,
            criteriaType: CriteriaType.SINGLE_DEVICE,
            criteriaTypeDescription: "",
            grade: Grade.NORMAL,
            gradeKoreanName: "보통",
            h2sValue: 20.0,
            nh3Value: 200.0,
            ch4Value: 5000.0,
            comparisonType: "이하",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 3,
            criteriaType: CriteriaType.SINGLE_DEVICE,
            criteriaTypeDescription: "",
            grade: Grade.BAD,
            gradeKoreanName: "나쁨",
            h2sValue: 20.0,
            nh3Value: 200.0,
            ch4Value: 10000.0,
            comparisonType: "이상",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 4,
            criteriaType: CriteriaType.SINGLE_DEVICE,
            criteriaTypeDescription: "",
            grade: Grade.VERY_BAD,
            gradeKoreanName: "매우나쁨",
            h2sValue: 2000.0,
            nh3Value: 15000.0,
            ch4Value: 20000.0,
            comparisonType: "이상",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
        ],
      },
      {
        criteriaType: CriteriaType.DEVICE_TOTAL,
        criteriaTypeDescription: CriteriaTypeDescription[CriteriaType.DEVICE_TOTAL],
        grades: [
          {
            id: 5,
            criteriaType: CriteriaType.DEVICE_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.GOOD,
            gradeKoreanName: "좋음",
            h2sValue: 5.0,
            nh3Value: 50.0,
            ch4Value: 3000.0,
            comparisonType: "이하",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 6,
            criteriaType: CriteriaType.DEVICE_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.NORMAL,
            gradeKoreanName: "보통",
            h2sValue: 20.0,
            nh3Value: 200.0,
            ch4Value: 5000.0,
            comparisonType: "이하",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 7,
            criteriaType: CriteriaType.DEVICE_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.BAD,
            gradeKoreanName: "나쁨",
            h2sValue: 20.0,
            nh3Value: 200.0,
            ch4Value: 10000.0,
            comparisonType: "이상",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 8,
            criteriaType: CriteriaType.DEVICE_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.VERY_BAD,
            gradeKoreanName: "매우나쁨",
            h2sValue: 2000.0,
            nh3Value: 15000.0,
            ch4Value: 20000.0,
            comparisonType: "이상",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
        ],
      },
      {
        criteriaType: CriteriaType.REGION_SINGLE,
        criteriaTypeDescription: CriteriaTypeDescription[CriteriaType.REGION_SINGLE],
        grades: [
          {
            id: 9,
            criteriaType: CriteriaType.REGION_SINGLE,
            criteriaTypeDescription: "",
            grade: Grade.GOOD,
            gradeKoreanName: "좋음",
            h2sValue: 5.0,
            nh3Value: 50.0,
            ch4Value: 3000.0,
            comparisonType: "이하",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 10,
            criteriaType: CriteriaType.REGION_SINGLE,
            criteriaTypeDescription: "",
            grade: Grade.NORMAL,
            gradeKoreanName: "보통",
            h2sValue: 20.0,
            nh3Value: 200.0,
            ch4Value: 5000.0,
            comparisonType: "이하",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 11,
            criteriaType: CriteriaType.REGION_SINGLE,
            criteriaTypeDescription: "",
            grade: Grade.BAD,
            gradeKoreanName: "나쁨",
            h2sValue: 20.0,
            nh3Value: 200.0,
            ch4Value: 10000.0,
            comparisonType: "이상",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 12,
            criteriaType: CriteriaType.REGION_SINGLE,
            criteriaTypeDescription: "",
            grade: Grade.VERY_BAD,
            gradeKoreanName: "매우나쁨",
            h2sValue: 2000.0,
            nh3Value: 15000.0,
            ch4Value: 20000.0,
            comparisonType: "이상",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
        ],
      },
      {
        criteriaType: CriteriaType.REGION_TOTAL,
        criteriaTypeDescription: CriteriaTypeDescription[CriteriaType.REGION_TOTAL],
        grades: [
          {
            id: 13,
            criteriaType: CriteriaType.REGION_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.GOOD,
            gradeKoreanName: "좋음",
            h2sValue: 5.0,
            nh3Value: 50.0,
            ch4Value: 3000.0,
            comparisonType: "이하",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 14,
            criteriaType: CriteriaType.REGION_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.NORMAL,
            gradeKoreanName: "보통",
            h2sValue: 20.0,
            nh3Value: 200.0,
            ch4Value: 5000.0,
            comparisonType: "이하",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 15,
            criteriaType: CriteriaType.REGION_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.BAD,
            gradeKoreanName: "나쁨",
            h2sValue: 20.0,
            nh3Value: 200.0,
            ch4Value: 10000.0,
            comparisonType: "이상",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 16,
            criteriaType: CriteriaType.REGION_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.VERY_BAD,
            gradeKoreanName: "매우나쁨",
            h2sValue: 2000.0,
            nh3Value: 15000.0,
            ch4Value: 20000.0,
            comparisonType: "이상",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
        ],
      },
      {
        criteriaType: CriteriaType.CITY_TOTAL,
        criteriaTypeDescription: CriteriaTypeDescription[CriteriaType.CITY_TOTAL],
        grades: [
          {
            id: 17,
            criteriaType: CriteriaType.CITY_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.GOOD,
            gradeKoreanName: "좋음",
            h2sValue: 5.0,
            nh3Value: 50.0,
            ch4Value: 3000.0,
            comparisonType: "이하",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 18,
            criteriaType: CriteriaType.CITY_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.NORMAL,
            gradeKoreanName: "보통",
            h2sValue: 20.0,
            nh3Value: 200.0,
            ch4Value: 5000.0,
            comparisonType: "이하",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 19,
            criteriaType: CriteriaType.CITY_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.BAD,
            gradeKoreanName: "나쁨",
            h2sValue: 20.0,
            nh3Value: 200.0,
            ch4Value: 10000.0,
            comparisonType: "이상",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
          {
            id: 20,
            criteriaType: CriteriaType.CITY_TOTAL,
            criteriaTypeDescription: "",
            grade: Grade.VERY_BAD,
            gradeKoreanName: "매우나쁨",
            h2sValue: 2000.0,
            nh3Value: 15000.0,
            ch4Value: 20000.0,
            comparisonType: "이상",
            isActive: true,
            createdAt: "",
            updatedAt: "",
          },
        ],
      },
    ];
  };

  // 값 변경 핸들러
  const handleValueChange = (
    criteriaType: CriteriaType,
    grade: Grade,
    field: "h2sValue" | "nh3Value" | "ch4Value",
    value: string
  ) => {
    const key = `${criteriaType}-${grade}`;
    const currentGradeData = editedValues[key] || getGradeData(criteriaType, grade);

    setEditedValues({
      ...editedValues,
      [key]: {
        ...currentGradeData,
        [field]: parseFloat(value) || 0,
      },
    });
  };

  // 입력 필드 포커스 핸들러 (기존 값 자동 선택)
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  // 현재 등급 데이터 가져오기
  const getGradeData = (criteriaType: CriteriaType, grade: Grade): GradeDataType => {
    const typeData = criteriaData.find((d) => d.criteriaType === criteriaType);
    const gradeData = typeData?.grades.find((g) => g.grade === grade);

    // 기본 comparisonType 설정
    const defaultComparisonType = grade === Grade.GOOD || grade === Grade.NORMAL ? "이하" : "이상";

    return {
      grade: grade,
      h2sValue: gradeData?.h2sValue || 0,
      nh3Value: gradeData?.nh3Value || 0,
      ch4Value: gradeData?.ch4Value || 0,
      comparisonType: (gradeData?.comparisonType || defaultComparisonType) as "이하" | "이상",
    };
  };

  // 저장 핸들러
  const handleSave = async (criteriaType: CriteriaType) => {
    try {
      setLoading(true);

      // 해당 타입의 모든 등급 데이터 수집
      const gradeData: GradeDataType[] = [Grade.GOOD, Grade.NORMAL, Grade.BAD, Grade.VERY_BAD].map(
        (grade) => {
          const key = `${criteriaType}-${grade}`;
          const data = editedValues[key] || getGradeData(criteriaType, grade);

          // comparisonType 자동 설정
          return {
            ...data,
            comparisonType:
              grade === Grade.GOOD || grade === Grade.NORMAL
                ? ("이하" as const)
                : ("이상" as const),
          };
        }
      );

      const bulkUpdateData: DataCriteriaBulkUpdateType = {
        criteriaType,
        gradeData,
      };

      await bulkUpdateDataCriteriaApi(bulkUpdateData);
      message.success("저장되었습니다.");

      // 데이터 새로고침
      await loadData();

      // 해당 타입의 편집된 값들만 초기화
      const newEditedValues = { ...editedValues };
      [Grade.GOOD, Grade.NORMAL, Grade.BAD, Grade.VERY_BAD].forEach((grade) => {
        delete newEditedValues[`${criteriaType}-${grade}`];
      });
      setEditedValues(newEditedValues);
    } catch (error) {
      message.error("저장 실패");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 데이터 카드 렌더링
  const renderDataCard = (
    label: string,
    unit: string,
    value: number,
    status: string,
    onChange: (value: string) => void
  ) => (
    <div className="data-card">
      <div className="data-label">
        {label}
        <br />({unit})
      </div>
      <input
        type="number"
        className="data-value"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleInputFocus}
        step="0.1"
        disabled={false}
      />
      <div className="data-status">{status}</div>
    </div>
  );

  // 위치 아이템 렌더링
  const renderLocationItem = (
    criteriaType: CriteriaType,
    grade: Grade,
    gradeData: GradeDataType
  ) => {
    const key = `${criteriaType}-${grade}`;
    const currentData = editedValues[key] || gradeData;

    // 등급에 따른 comparisonType 자동 설정
    const comparisonType = grade === Grade.GOOD || grade === Grade.NORMAL ? "이하" : "이상";

    return (
      <div className="location-item" key={grade}>
        <h3 className={`location-title ${GradeColorClass[grade]}`}>
          등급 : {GradeKoreanName[grade]}
        </h3>
        <div className="data-cards">
          {renderDataCard("황화수소", "H₂S, ppm", currentData.h2sValue, comparisonType, (value) =>
            handleValueChange(criteriaType, grade, "h2sValue", value)
          )}
          {renderDataCard("암모니아", "NH₃, ppm", currentData.nh3Value, comparisonType, (value) =>
            handleValueChange(criteriaType, grade, "nh3Value", value)
          )}
          {renderDataCard("메탄가스", "CH₄, ppm", currentData.ch4Value, comparisonType, (value) =>
            handleValueChange(criteriaType, grade, "ch4Value", value)
          )}
        </div>
      </div>
    );
  };

  // 측정 박스 렌더링
  const renderMeasurementBox = (criteriaGroup: DataCriteriaGroupResponseType) => {
    const subtitle = getSubtitle(criteriaGroup.criteriaType);
    const infoText = getInfoText(criteriaGroup.criteriaType);

    return (
      <div className="measurement-box" key={criteriaGroup.criteriaType}>
        <div className="measurement-header">
          <div className="icon-wrapper"></div>
          <h2 className="measurement-title">
            {criteriaGroup.criteriaTypeDescription}
            {subtitle && <span className="subtitle">{subtitle}</span>}
          </h2>
          <button
            className="close-btn save-btn"
            onClick={() => handleSave(criteriaGroup.criteriaType)}
            disabled={loading}
          >
            저장
          </button>
        </div>

        <div className="measurement-content">
          <div className="location-grid">
            {[Grade.GOOD, Grade.NORMAL, Grade.BAD, Grade.VERY_BAD].map((grade) => {
              const gradeData = criteriaGroup.grades.find((g) => g.grade === grade);
              if (!gradeData) return null;

              return renderLocationItem(criteriaGroup.criteriaType, grade, {
                grade: gradeData.grade,
                h2sValue: gradeData.h2sValue,
                nh3Value: gradeData.nh3Value,
                ch4Value: gradeData.ch4Value,
                comparisonType: gradeData.comparisonType as "이하" | "이상",
              });
            })}
          </div>

          <div className="info-box">
            <span className="info-icon">ℹ</span>
            <div className="info-text">{infoText}</div>
          </div>
        </div>
      </div>
    );
  };

  // 부제목 가져오기
  const getSubtitle = (criteriaType: CriteriaType): string => {
    switch (criteriaType) {
      case CriteriaType.SINGLE_DEVICE:
        return "(실시간모니터링화면-지도의 아이콘 클릭시 측정물질별 등급표기)";
      case CriteriaType.DEVICE_TOTAL:
        return "(기존에 삭제했다가 다시 추가해야하는항목-수치는 단일측정물질과 값 동일)";
      case CriteriaType.REGION_SINGLE:
      case CriteriaType.REGION_TOTAL:
        return "(통계분석화면 / 실시간모니터링화면 / 대시보드화면 - 등급표기)";
      case CriteriaType.CITY_TOTAL:
        return "(통계분석화면 / 실시간모니터링화면 / 대시보드화면 - 등급표기)";
      default:
        return "";
    }
  };

  // 정보 텍스트 가져오기
  const getInfoText = (criteriaType: CriteriaType): React.ReactNode => {
    switch (criteriaType) {
      case CriteriaType.SINGLE_DEVICE:
        return (
          <>
            <strong>센서별 측정물질 단일</strong>
            <br />각 수치값이 초과되면 다음단계 등급이 표기됩니다.
          </>
        );
      case CriteriaType.DEVICE_TOTAL:
        return (
          <>
            <strong>센서별 측정물질 종합</strong>
            <br />
            각 등급단계별 기입된 수치값이 2가지 이상 초과될 경우 다음단계 등급으로 표기합니다.
            <br />
            (황화수소+암모니아)의 값으로만 등급 표기를 합니다.
          </>
        );
      case CriteriaType.REGION_SINGLE:
        return (
          <>
            <strong>지역평균 측정물질 단일</strong>
            <br />
            <span>지역별 모든기기통합 평균값 기입</span>
            <br />각 수치값이 초과되면 다음단계 등급이 표기됩니다.
          </>
        );
      case CriteriaType.REGION_TOTAL:
        return (
          <>
            <strong>지역평균 측정물질 종합</strong>
            <br />
            <span>지역의 모든기기통합 평균값 기입</span>
            <br />
            각 등급단계별 기입된 수치값이 2가지 이상 초과될 경우 다음단계 등급으로 표기합니다.
            <br />
            (황화수소+암모니아)의 값으로만 등급 표기를 합니다.
          </>
        );
      case CriteriaType.CITY_TOTAL:
        return (
          <>
            <strong>도시평균 측정물질 종합</strong>
            <br />
            <span>도시의 모든기기통합 평균값 기입</span>
            <br />
            각 등급단계별 기입된 수치값이 2가지 이상 초과될 경우 다음단계 등급으로 표기합니다.
            <br />
            (황화수소+암모니아)의 값으로만 등급 표기를 합니다.
          </>
        );
      default:
        return "";
    }
  };

  // 순서대로 정렬
  const orderedCriteriaTypes = [
    CriteriaType.SINGLE_DEVICE,
    CriteriaType.DEVICE_TOTAL,
    CriteriaType.REGION_SINGLE,
    CriteriaType.REGION_TOTAL,
    CriteriaType.CITY_TOTAL,
  ];

  const sortedCriteriaData = orderedCriteriaTypes
    .map((type) => criteriaData.find((c) => c.criteriaType === type))
    .filter(Boolean) as DataCriteriaGroupResponseType[];

  return (
    <div className="device-list-container">
      {loading ? (
        <div className="loading">데이터 로딩 중...</div>
      ) : (
        sortedCriteriaData.map((criteriaGroup) => renderMeasurementBox(criteriaGroup))
      )}
    </div>
  );
};

export default DataCriteriaView;
